/*
 * To add a matTable in component:
 * ```
 * protected table = inject(MatTableController<YourDataType>);
 *
 * constructor() {
 *  this.table.config.set([<YourColumnConfigs>]);
 *
 * // Optional: update dataSource reactively when rawData, filterQuery or sortState changes
 *  effect(() => {
 *    this.refreshDataSource();
 *  });
 * }
 *
 * refreshDataSource() {
 *  this.table.rawData.set([{items: <YourDataArray>}]);
 *  this.table.filterQuery.set(this.searchText() ?? '');
 * }
 * ```
 */
import { formatCurrency, formatDate, formatNumber } from '@angular/common';
import { computed, inject, Injectable, LOCALE_ID, signal, TemplateRef } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { PageEvent } from 'node_modules/@angular/material/paginator';
import { SharedService } from './Shared/shared-service.service';

export interface ColumnConfig<T = any> {
  columnDef: string;
  header: string;
  property?: string;
  calculated?: (item: T) => any;
  type?: 'date' | 'currency' | 'boolean' | 'number' | 'index';
  format?: string | ((value: any) => string);
  filterBy?: 'value' | 'formatted' | 'both';
  templateRef?: TemplateRef<any>;
  onClick?: (row: T) => void;
  footer?: (data: T[]) => any;
  show?: boolean;
  canHide?: boolean;
  cssClass?: {
    header?: string;
    cell?: string;
    footer?: string;
  };
}

export interface TableGroup<T> {
  headerRow?: any;
  items: T[];
}

@Injectable()
export class MatTableController<T> {
  private readonly locale = inject(LOCALE_ID);
  private sharedService = inject(SharedService);

  readonly rawData = signal<TableGroup<T>[]>([]);
  readonly config = signal<ColumnConfig<T>[]>([]);
  readonly sortState = signal<Sort>({active: '', direction: ''});
  readonly filterQuery = signal<string>('');

  readonly hideGroupHeader = signal<boolean>(false); // hide group header if item.length === 0
  readonly paginationEnabled = signal<boolean>(false);
  readonly pageIndex = signal<number>(0);
  readonly pageSize = signal<number>(25);
  readonly pageLength = computed(() => this.flattenedData().length);

  readonly flattenedData = computed(() => {
    let data = this.sharedService.deepClone(this.rawData());
    const query = this.filterQuery().toLowerCase();
    const visible = this.visibleConfig();

    if (query) {
      data = data.map(d => ({
        ...d,
        items: this.filterItems(query, d.items, visible)
      }));
    }
    const activeSort = this.sortState();
    const col = this.config().find(c => c.columnDef === activeSort.active);

    return data.flatMap(d => [...(d.headerRow && (!this.hideGroupHeader() || d.items.length > 0) ? [d.headerRow] : []), ...(!col || !activeSort.direction ? d.items : this.sortItems(d.items, col, activeSort.direction))]);
  });

  readonly dataSource = computed(() => {
    if (this.paginationEnabled()) {
      const start = this.pageIndex() * this.pageSize();
      return this.flattenedData().slice(start, start + this.pageSize());
    } else return this.flattenedData();
  })

  readonly configToggle = computed(() => this.config().filter(c => c.canHide !== false));
  readonly visibleConfig = computed(() => this.config().filter(c => c.canHide === false || (c.show ?? true)));
  readonly columnDefs = computed(() => this.config().flatMap(c => c.canHide === false || c.show !== false ? c.columnDef : []));

  toggleColVisibility(columnDef: string) {
    this.config.update(configs =>
      configs.map(col => col.columnDef === columnDef && col.canHide !== false ? { ...col, show: !(col.show ?? true) } : col)
    );
  }

  getRawValue(row: any, config: ColumnConfig<T>): any {
    return config.calculated ? config.calculated(row) : config.property ? config.property.split('.').reduce((o, i) => o?.[i], row) : row[config.columnDef];
  }

  formatValue(row: any, config: ColumnConfig, isFooter: boolean = false): any {
    let value;
    let type = config.type;
    if (isFooter) {
      value = config.footer?.(row);
    } else {
      value = this.getRawValue(row, config);
      if (type === 'currency') type = 'number';
    }

    if (typeof config.format === 'function') {
      return config.format(value);
    }

    switch (type) {
      case 'date':
        return formatDate(new Date(value), config.format || 'dd/MM/yyyy', this.locale);
      case 'number':
        return formatNumber(value, this.locale, config.format || '1.2-2');
      case 'currency':
        return formatCurrency(value, this.locale, 'RM', 'MYR', config.format || '1.2-2');
      default:
        return value;
    }
  }

  filterItems(query: string, items: T[], visibleColumns: ColumnConfig<T>[]): T[] {
    return items.filter(item =>
      visibleColumns.some(col => {
        const value = this.getRawValue(item, col)?.toString().toLowerCase();
        let formatted = '';
        if (col.filterBy === 'formatted' || col.filterBy === 'both')
          formatted = this.formatValue(item, col)?.toString().toLowerCase();
        const filterValue = col.filterBy === 'both' ? [formatted, value].join(' | ') : col.filterBy === 'formatted' ? formatted : value;
        return filterValue?.includes(query);
      })
    )
  }

  sortItems<T>(items: T[], col: ColumnConfig, direction: string): any[] {
    return items.sort((a, b) => {
      const valA = this.getRawValue(a, col);
      const valB = this.getRawValue(b, col);
      const multiplier = direction === 'asc' ? 1 : -1;
      return this.compareValues(valA, valB) * multiplier;
    });
  }

  compareValues(a: any, b: any): number {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;

    if (a instanceof Date && b instanceof Date) {
      return (a.getTime() - b.getTime());
    }

    if (typeof a === 'boolean' && typeof b === 'boolean') {
      return (a === b ? 0 : a ? 1 : -1);
    }

    if (typeof a === 'number' && typeof b === 'number') {
      return (a - b);
    }

    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b);
    }

    // Fallback
    return String(a).localeCompare(String(b));
  }

  onPage(event: PageEvent) {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }
}
